import React, { useCallback, useEffect, useState } from 'react';
import '../assets/Applications.css';
import logoIcon from '../assets/logo/icon.svg';

interface ShopApplication {
  id: string;
  user_id: string;
  business_name: string;
  business_type: string;
  shop_link?: string;
  latitude?: number;
  longitude?: number;
  address?: string;
  orders_per_day?: string;
  phone?: string;
  status: 'pending' | 'approved' | 'declined';
  decline_reason?: string;
  created_at: string;
}

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

export const Applications: React.FC = () => {
  const [applications, setApplications] = useState<ShopApplication[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<'all' | 'pending' | 'approved' | 'declined'>('pending');
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [declineReason, setDeclineReason] = useState('');
  const [showDeclineModal, setShowDeclineModal] = useState<string | null>(null);

  const fetchApplications = useCallback(async () => {
    setLoading(true);
    try {
      const endpoint = filter === 'all'
        ? `${API_BASE_URL}/shop-applications/all`
        : filter === 'pending'
          ? `${API_BASE_URL}/shop-applications/pending`
          : `${API_BASE_URL}/shop-applications/all`;

      const response = await fetch(endpoint, { credentials: 'include' });
      const data = await response.json();

      if (data.success) {
        let filtered = data.data;
        if (filter !== 'all' && filter !== 'pending') {
          filtered = data.data.filter((app: ShopApplication) => app.status === filter);
        }
        setApplications(filtered);
      }
    } catch (error) {
      console.error('Error fetching applications:', error);
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    fetchApplications();
  }, [fetchApplications]);

  const handleApprove = async (id: string) => {
    setActionLoading(id);
    try {
      const response = await fetch(`${API_BASE_URL}/shop-applications/${id}`, {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: 'approved' }),
      });

      const data = await response.json();
      if (data.success) {
        fetchApplications();
      }
    } catch (error) {
      console.error('Error approving application:', error);
    } finally {
      setActionLoading(null);
    }
  };

  const handleDecline = async (id: string) => {
    setActionLoading(id);
    try {
      const response = await fetch(`${API_BASE_URL}/shop-applications/${id}`, {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          status: 'declined',
          decline_reason: declineReason || 'Application did not meet requirements'
        }),
      });

      const data = await response.json();
      if (data.success) {
        setShowDeclineModal(null);
        setDeclineReason('');
        fetchApplications();
      }
    } catch (error) {
      console.error('Error declining application:', error);
    } finally {
      setActionLoading(null);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (status: string) => {
    const badges: Record<string, { className: string; label: string }> = {
      pending: { className: 'badge-pending', label: '⏳ Pending' },
      approved: { className: 'badge-approved', label: '✓ Approved' },
      declined: { className: 'badge-declined', label: '✗ Declined' }
    };
    return badges[status] || { className: '', label: status };
  };


  return (
    <div className="page">
      <header className="page__head">
        <img src={logoIcon} alt="" className="page__logo" onError={(e) => e.currentTarget.style.display = 'none'} />
        <div className="page__titles">
          <h1 className="page__title">Applications</h1>
          <p className="page__sub">Shops requesting to join Hypr</p>
        </div>
      </header>

      <div className="filter-tabs">
        <button
          className={`filter-tab ${filter === 'pending' ? 'active' : ''}`}
          onClick={() => setFilter('pending')}
        >
          Pending
        </button>
        <button
          className={`filter-tab ${filter === 'approved' ? 'active' : ''}`}
          onClick={() => setFilter('approved')}
        >
          Approved
        </button>
        <button
          className={`filter-tab ${filter === 'declined' ? 'active' : ''}`}
          onClick={() => setFilter('declined')}
        >
          Declined
        </button>
        <button
          className={`filter-tab ${filter === 'all' ? 'active' : ''}`}
          onClick={() => setFilter('all')}
        >
          All
        </button>
      </div>

      <div className="applications-scroll-container">
        {loading ? (
          <div className="loading-state">Loading applications...</div>
        ) : applications.length === 0 ? (
          <div className="empty-state">
            <p>No {filter === 'all' ? '' : filter} applications found</p>
          </div>
        ) : (
          <div className="applications-list">
            {applications.map((app) => {
              const badge = getStatusBadge(app.status);
              return (
                <div key={app.id} className="application-card">
                  <div className="application-header">
                    <div className="business-info">
                      <h3>{app.business_name}</h3>
                      <span className={`status-badge ${badge.className}`}>
                        {badge.label}
                      </span>
                    </div>
                    <span className="application-date">{formatDate(app.created_at)}</span>
                  </div>

                  <div className="application-details">
                    <div className="detail-row">
                      <span className="detail-label">Business Type:</span>
                      <span className="detail-value">{app.business_type}</span>
                    </div>
                    <div className="detail-row">
                      <span className="detail-label">Phone:</span>
                      <span className="detail-value">{app.phone || 'N/A'}</span>
                    </div>
                    <div className="detail-row">
                      <span className="detail-label">Orders/Day:</span>
                      <span className="detail-value">{app.orders_per_day || 'N/A'}</span>
                    </div>
                    {app.shop_link && (
                      <div className="detail-row">
                        <span className="detail-label">Website:</span>
                        <a href={app.shop_link} target="_blank" rel="noopener noreferrer" className="detail-link">
                          {app.shop_link}
                        </a>
                      </div>
                    )}
                    {app.address && (
                      <div className="detail-row">
                        <span className="detail-label">Address:</span>
                        <span className="detail-value">{app.address}</span>
                      </div>
                    )}
                    {app.latitude && app.longitude && (
                      <div className="detail-row">
                        <span className="detail-label">Coordinates:</span>
                        <span className="detail-value">
                          {app.latitude.toFixed(6)}, {app.longitude.toFixed(6)}
                        </span>
                      </div>
                    )}
                    {app.decline_reason && (
                      <div className="detail-row decline-reason">
                        <span className="detail-label">Decline Reason:</span>
                        <span className="detail-value">{app.decline_reason}</span>
                      </div>
                    )}
                  </div>

                  {app.status === 'pending' && (
                    <div className="application-actions">
                      <button
                        className="btn-approve"
                        onClick={() => handleApprove(app.id)}
                        disabled={actionLoading === app.id}
                      >
                        {actionLoading === app.id ? 'Processing...' : '✓ Approve'}
                      </button>
                      <button
                        className="btn-decline"
                        onClick={() => setShowDeclineModal(app.id)}
                        disabled={actionLoading === app.id}
                      >
                        ✗ Decline
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Decline Modal */}
      {showDeclineModal && (
        <div className="modal-overlay" onClick={() => setShowDeclineModal(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>Decline Application</h3>
            <p>Please provide a reason for declining this application:</p>
            <textarea
              value={declineReason}
              onChange={(e) => setDeclineReason(e.target.value)}
              placeholder="Enter reason..."
              rows={3}
            />
            <div className="modal-actions">
              <button
                className="btn-secondary"
                onClick={() => {
                  setShowDeclineModal(null);
                  setDeclineReason('');
                }}
              >
                Cancel
              </button>
              <button
                className="btn-decline"
                onClick={() => handleDecline(showDeclineModal)}
                disabled={actionLoading === showDeclineModal}
              >
                {actionLoading === showDeclineModal ? 'Processing...' : 'Decline'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
